import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ButtonCondensed,
  InputText,
  Text,
  InputTextArea,
} from '@companycam/slab-web';
import styled from 'styled-components';

import { translate } from 'translations/i18n';
import InputClipboard, {
  messages as inputClipboardMessages,
} from 'components/shared/DesignSystem/SlabTransferPortal/InputClipboard';
import PaymentRequestStatusBadge from '../PaymentRequestStatusBadge';
import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import Steven from 'helpers/steven';

import { MINIMUM_CHARGE_IN_CENTS, MAXIMUM_CHARGE_IN_CENTS } from '../constants';
import { stripFormatting } from '../helpers';

import {
  BasePaymentRequestInput,
  PaymentRequestStatus,
  TrustyCheckoutSessionLink,
  PaymentRequestFormValues,
} from '../types';

export type PaymentRequestModalProps = {
  defaultFormValues: PaymentRequestFormValues;
  modalTitle: string;
  onRequestClose: () => void;
  status: PaymentRequestStatus;
  trustyCheckoutSessionLink: TrustyCheckoutSessionLink;
  onSubmit?: (values: BasePaymentRequestInput) => Promise<void>;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
};

// #region styled-components

const ContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
`;
const BadgeStatusDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: var(--cc_size_spacing_s);
`;
// #endregion

const generatePaymentRequestSchema = () => {
  const limitErrorMessage = translate('paymentRequest.modal.totalRequired');

  return Yup.object().shape({
    title: Yup.string().required(
      translate('paymentRequest.modal.titleRequired'),
    ),
    total: Yup.number()
      .transform((value, originalValue, context) => {
        if (context.isType(value)) return value;

        const newValue = stripFormatting(originalValue);

        return context.isType(newValue) ? newValue : 0.0;
      })
      .min(MINIMUM_CHARGE_IN_CENTS / 100, limitErrorMessage)
      .max(MAXIMUM_CHARGE_IN_CENTS / 100, limitErrorMessage)
      .when('status', {
        is: 'shared',
        then: (schema) => schema.required(limitErrorMessage),
        otherwise: (schema) => schema,
      }),
    memo: Yup.string().nullable(),
    status: Yup.string().oneOf(['draft', 'shared']),
  });
};

const formatInputToCurrency = (value: string) => {
  const number = stripFormatting(value);

  if (Number.isNaN(number)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(number);
};

const FormHeading = ({ text }: { text: string }) => {
  return (
    <Text
      as="span"
      size="s"
      ccMargin="none none s"
      family="heading"
      weight="700"
    >
      {text}
    </Text>
  );
};

const ReadOnlyFormValue = ({ value }: { value: string }) => {
  return (
    <Text
      as="span"
      data-testid="payment-request__modal__read-only-value"
      size="m"
      ccMargin="s none"
      family="heading"
      color="nonessential"
      weight="600"
    >
      {value}
    </Text>
  );
};

const PaymentRequestModal = ({
  onRequestClose,
  defaultFormValues,
  modalTitle,
  status,
  trustyCheckoutSessionLink,
  onSubmit,
  primaryActionLabel = '',
  secondaryActionLabel = '',
}: PaymentRequestModalProps) => {
  const { title, amount, memo } = defaultFormValues;
  const isReadOnly = [
    'paid',
    'payment_processing',
    'refund_pending',
    'refunded',
  ].includes(status);

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      title,
      total: amount.length > 0 ? formatInputToCurrency(amount) : '',
      memo,
      status,
    },
    validationSchema: generatePaymentRequestSchema(),
    onSubmit: async (values, { setSubmitting }) => {
      const params: BasePaymentRequestInput = {
        status: values.status,
        title: values.title,
        amount: stripFormatting(values.total).toString(),
        memo: values.memo,
      };

      await onSubmit?.(params);
      setSubmitting(false);
    },
  });

  const onSharePress = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await formik.setFieldValue('status', 'shared');
    formik.handleSubmit();
  };

  const onSaveDraftPress = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await formik.setFieldValue('status', 'draft');
    formik.handleSubmit();
  };

  const primaryAction = isReadOnly
    ? {
        label: translate('common.close'),
        onClick: onRequestClose,
      }
    : {
        label: primaryActionLabel,
        onClick: onSharePress,
        disabled: formik.isSubmitting,
        'data-testid': 'payment-request__modal__primary-button',
      };

  const secondaryAction = isReadOnly
    ? null
    : {
        label: secondaryActionLabel,
        onClick: onSaveDraftPress,
        disabled: formik.isSubmitting,
        'data-testid': 'payment-request__modal__secondary-button',
      };

  return (
    <CCModal
      isOpen
      showCloseButton={false}
      size="small"
      onRequestClose={onRequestClose}
      headerStyle={{
        textAlign: 'center',
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingTop: 'var(--cc_size_spacing_xl)',
        paddingBottom: 'var(--cc_size_spacing_xs)',
        borderBottomWidth: 0,
      }}
      bodyStyle={{
        paddingTop: 'var(--cc_size_spacing_l)',
        paddingBottom: 'var(--cc_size_spacing_xl)',
      }}
      footerStyle={{
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingLeft: 'var(--cc_size_spacing_xl)',
        paddingTop: 'var(--cc_size_spacing_l)',
        paddingBottom: 'var(--cc_size_spacing_l)',
      }}
      title={modalTitle}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      footerContentBefore={
        isReadOnly ? null : (
          <ButtonCondensed
            onClick={onRequestClose}
            color="subtle"
            data-testid="payment-request__modal__cancel-button"
          >
            {translate('common.cancel')}
          </ButtonCondensed>
        )
      }
    >
      <ContentDiv>
        <BadgeStatusDiv data-testid="payment-request__modal__status-label">
          <FormHeading text={translate('paymentRequest.modal.statusHeader')} />
          <PaymentRequestStatusBadge status={status} />
        </BadgeStatusDiv>
        <label>
          {isReadOnly ? (
            <ReadOnlyFormValue value={formik.values.title} />
          ) : (
            <InputText
              id="title"
              label={translate('common.title')}
              message={formik.touched.title ? formik.errors.title : null}
              name="title"
              placeholder={translate('common.title')}
              onChange={formik.handleChange}
              value={formik.values.title}
              status={
                formik.touched.title && formik.errors.title ? 'error' : 'normal'
              }
              data-testid="payments__payment-request-modal__title-input"
            />
          )}
        </label>
        <label>
          {isReadOnly ? (
            <ReadOnlyFormValue value={formik.values.total} />
          ) : (
            <InputText
              id="total"
              label={translate('paymentRequest.modal.total')}
              name="total"
              message={formik.touched.total ? formik.errors.total : null}
              placeholder={translate('paymentRequest.modal.totalPlaceholder')}
              type="text"
              onChange={formik.handleChange}
              onBlur={() => {
                if (!formik.values.total) {
                  return;
                }
                formik.setFieldValue(
                  'total',
                  formatInputToCurrency(formik.values.total),
                );
              }}
              value={formik.values.total}
              status={
                formik.touched.total && formik.errors.total ? 'error' : 'normal'
              }
              data-testid="payments__payment-request-modal__total-input"
            />
          )}
        </label>
        <label>
          {isReadOnly ? (
            <ReadOnlyFormValue value={formik.values.memo || 'N/A'} />
          ) : (
            <InputTextArea
              id="memo"
              label={translate('paymentRequest.modal.memo')}
              inputSize="large"
              name="memo"
              placeholder={translate('paymentRequest.modal.memoPlaceholder')}
              onChange={formik.handleChange}
              value={formik.values.memo}
              data-testid="payments__payment-request-modal__memo-input"
            />
          )}
        </label>
        {trustyCheckoutSessionLink ? (
          <div>
            <FormHeading
              text={translate('paymentRequest.modal.clipboardTitle')}
            />
            <InputClipboard
              text={trustyCheckoutSessionLink}
              onCopySuccess={() => {
                Steven.elementClicked({
                  id: 'payments_copy_checkout_url',
                  content: inputClipboardMessages.copy.defaultMessage,
                  type: 'button',
                  action:
                    'copy_checkout_url_from_payment_request_modal_clicked',
                });
              }}
            />
          </div>
        ) : null}
      </ContentDiv>
    </CCModal>
  );
};

export default PaymentRequestModal;
